// Сессия общения с агентом через HTTP-мост agent-bridge.
// Аудио НЕ передаётся: голос распознаёт и озвучивает сам телефон (Siri).
// Сюда уходит только ТЕКСТ реплики, обратно — текст ответа агента.

import { agentUrl, authHeaders, config } from '../config'
import { clearAccessKey, getAccessKey } from './access'
import { getMeetingAgent, getMode, resolveAgent, resolveAgentById } from './mode'
import { getSpeed, getTtsEngine } from './settings'
import type {
  ConnectionState,
  SessionEvent,
  SessionEventHandler,
  VoiceSession,
} from '../types'

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes.buffer
}

// Стабильный идентификатор клиента — чтобы агент помнил контекст между сообщениями.
function getClientId(): string {
  const KEY = 'clawapp:clientId'
  try {
    let id = localStorage.getItem(KEY)
    if (!id) {
      id = 'c' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4)
      localStorage.setItem(KEY, id)
    }
    return id
  } catch {
    return 'default'
  }
}

export class BridgeSession implements VoiceSession {
  private handlers = new Set<SessionEventHandler>()
  private _state: ConnectionState = 'idle'
  private clientId = getClientId()
  private inflight: AbortController | null = null
  private confTimer: number | null = null
  private confCancelled = false

  get state(): ConnectionState {
    return this._state
  }

  on(handler: SessionEventHandler): () => void {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  private emit(e: SessionEvent): void {
    this.handlers.forEach((h) => h(e))
  }

  private setState(state: ConnectionState, detail?: string): void {
    this._state = state
    this.emit({ type: 'connection', state, detail })
  }

  connect(): void {
    this.setState('connecting')
    const url = config.apiBaseUrl + '/api/health'
    fetch(url, { headers: { ...authHeaders() } })
      .then((r) => this.setState(r.ok ? 'connected' : 'error', r.ok ? undefined : `HTTP ${r.status}`))
      .catch((e) => this.setState('error', String(e?.message ?? e)))
  }

  disconnect(): void {
    this.confCancelled = true
    if (this.confTimer != null) {
      clearTimeout(this.confTimer)
      this.confTimer = null
    }
    this.inflight?.abort()
    this.inflight = null
    this.setState('closed')
  }

  // Аудио не используется в этом режиме.
  sendAudio(): void {}

  sendText(text: string): void {
    const trimmed = text.trim()
    if (!trimmed) return

    const mode = getMode()
    const who = resolveAgent(mode, getMeetingAgent())

    // Показать мою реплику в истории.
    this.emit({ type: 'user_transcript', text: trimmed, final: true })
    // Агент «думает» — UI показывает статус и глушит прошлый ответ.
    this.emit({ type: 'agent_speech_start', agent: who })
    this.emit({
      type: 'agent_status',
      status: { state: 'working', task: `${who.name} думает…`, updatedAt: Date.now() },
    })

    // «Конференция» — асинхронно (фон + опрос), чтобы не упираться в таймаут.
    if (mode === 'conference') {
      this.startConference(trimmed)
      return
    }

    // Обычный режим — один запрос/ответ.
    this.inflight?.abort()
    const ctrl = new AbortController()
    this.inflight = ctrl

    fetch(agentUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        text: trimmed,
        clientId: this.clientId,
        accessKey: getAccessKey(),
        mode,
        agentId: mode === 'meeting' ? getMeetingAgent() : undefined,
        speed: getSpeed(),
        ttsEngine: getTtsEngine(),
      }),
      signal: ctrl.signal,
    })
      .then((r) => this.checkResp(r))
      .then((data: { reply?: string; audioB64?: string }) => {
        const reply = (data?.reply || '').trim()
        this.emit({
          type: 'agent_message',
          agent: who,
          text: reply || '(агент не вернул текстовый ответ — попробуй переформулировать)',
          audio: data?.audioB64 ? base64ToArrayBuffer(data.audioB64) : undefined,
        })
        this.emit({ type: 'agent_speech_end' })
        this.emit({ type: 'agent_status', status: { state: 'done', task: 'Ответ готов', updatedAt: Date.now() } })
      })
      .catch((e) => {
        if ((e as Error).name === 'AbortError') return
        this.emit({ type: 'agent_speech_end' })
        this.emit({ type: 'error', message: 'Агент не ответил: ' + String((e as Error).message) })
      })
      .finally(() => {
        if (this.inflight === ctrl) this.inflight = null
      })
  }

  private async checkResp(r: Response) {
    if (r.status === 401) {
      clearAccessKey()
      // Вернуть экран входа сразу, без перезагрузки страницы (см. App.tsx).
      try {
        window.dispatchEvent(new Event('clawapp:locked'))
      } catch {
        /* noop */
      }
      throw new Error('Доступ запрещён. Введите кодовую фразу заново.')
    }
    if (!r.ok) {
      const detail = await r.text().catch(() => '')
      throw new Error(`HTTP ${r.status} ${detail.slice(0, 120)}`)
    }
    return r.json()
  }

  // ── «Конференция»: запуск фоновой задачи и опрос готовых реплик ──────────────

  private startConference(text: string): void {
    this.confCancelled = false
    const base = config.apiBaseUrl
    fetch(base ? base + '/api/conference/start' : '/api/conference/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ text, clientId: this.clientId, accessKey: getAccessKey(), speed: getSpeed(), ttsEngine: getTtsEngine() }),
    })
      .then((r) => this.checkResp(r))
      .then((data: { jobId?: string }) => {
        if (!data?.jobId) throw new Error('конференция не запустилась')
        this.pollConference(data.jobId, 0, Date.now())
      })
      .catch((e) => {
        this.emit({ type: 'agent_speech_end' })
        this.emit({ type: 'error', message: 'Конференция не запустилась: ' + String((e as Error).message) })
      })
  }

  private pollConference(jobId: string, emitted: number, startedAt: number): void {
    if (this.confCancelled) return
    if (Date.now() - startedAt > 240000) {
      this.emit({ type: 'agent_speech_end' })
      this.emit({ type: 'error', message: 'Конференция заняла слишком долго.' })
      return
    }
    const base = config.apiBaseUrl
    const url = (base ? base : '') + '/api/conference/poll?id=' + encodeURIComponent(jobId)
    fetch(url, { headers: { ...authHeaders() } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: { messages?: Array<{ agentId: string; text: string; audioB64?: string }>; done?: boolean; error?: string | null; pending?: string[] }) => {
        if (this.confCancelled) return
        const messages = Array.isArray(data?.messages) ? data.messages : []
        let i = emitted
        for (; i < messages.length; i++) {
          const m = messages[i]
          const text = (m.text || '').trim()
          if (!text) continue
          this.emit({
            type: 'agent_message',
            agent: resolveAgentById(m.agentId),
            text,
            audio: m.audioB64 ? base64ToArrayBuffer(m.audioB64) : undefined,
          })
        }
        if (data?.done) {
          if (data.error && i === 0) this.emit({ type: 'error', message: 'Конференция: ' + data.error })
          this.emit({ type: 'agent_speech_end' })
          this.emit({ type: 'agent_status', status: { state: 'done', task: 'Ответ готов', updatedAt: Date.now() } })
          return
        }
        // Показываем, КОГО ждём (следующий специалист), а не «Координатора».
        const pending = Array.isArray(data?.pending) ? data.pending : []
        this.emit({ type: 'agent_thinking', agent: pending.length ? resolveAgentById(pending[0]) : undefined })
        this.confTimer = window.setTimeout(() => this.pollConference(jobId, i, startedAt), 1500)
      })
      .catch(() => {
        // Сетевой сбой опроса — повторяем.
        if (!this.confCancelled) {
          this.confTimer = window.setTimeout(() => this.pollConference(jobId, emitted, startedAt), 2500)
        }
      })
  }

  interrupt(): void {
    this.confCancelled = true
    if (this.confTimer != null) {
      clearTimeout(this.confTimer)
      this.confTimer = null
    }
    this.inflight?.abort()
    this.inflight = null
    this.emit({ type: 'interrupted' })
  }
}
